
import { supabase } from '@/integrations/supabase/client';
import { Payment } from '@/services/types';
import { mockPayments } from '@/services/mockData/mockPayments';
import logger from '@/utils/logger';

export interface PaymentDataResult {
  payments: Payment[];
  dataSource: 'supabase' | 'mock';
  connectionError: string | null;
}

export interface PaginatedPaymentDataResult {
  payments: Payment[];
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  dataSource: 'supabase' | 'mock';
  connectionError: string | null;
}

export interface PaymentPaginationParams {
  page: number;
  pageSize: number;
  searchQuery?: string;
  statusFilter?: string;
}

/**
 * Получает все данные о платежах из Supabase или мок-данные без пагинации
 */
export const fetchPaymentData = async (): Promise<PaymentDataResult> => {
  try {
    const { data: paymentsData, error } = await supabase
      .from('payments')
      .select(`
        *,
        rentals (
          human_number
        ),
        couriers (
          name
        )
      `)
      .order('payment_date', { ascending: false });
    
    if (error) {
      console.error('Ошибка при получении данных о платежах из Supabase:', error);
      throw error;
    }

    // Обработка и форматирование данных из Supabase
    const payments: Payment[] = paymentsData.map(paymentRecord => {
      return {
        id: paymentRecord.id,
        rentalId: paymentRecord.rental_id || '',
        rentalHumanNumber: paymentRecord.rentals?.human_number || null,
        courierId: paymentRecord.courier_id,
        courierName: paymentRecord.couriers?.name || 'Неизвестный курьер',
        amount: paymentRecord.amount,
        date: paymentRecord.payment_date,
        status: paymentRecord.status as 'paid' | 'pending' | 'error' | 'completed' | 'auth' | 'failed',
        method: paymentRecord.payment_method as 'card' | 'cash' | 'account' | 'sbp',
        human_number: paymentRecord.human_number || null,
        receiptUrl: paymentRecord.receipt_url || null,
        subscription_id: paymentRecord.subscription_id || null
      };
    });

    logger.info(`Получены данные о платежах из Supabase: ${payments.length}`);
    return {
      payments,
      dataSource: 'supabase',
      connectionError: null
    };
  } catch (error: any) {
    console.error('Ошибка при получении данных о платежах, возвращаем мок-данные:', error);

    return {
      payments: mockPayments,
      dataSource: 'mock',
      connectionError: error.message
    };
  }
};

/**
 * Получает платежи с пагинацией и серверным поиском
 */
export const fetchPaymentDataPaginated = async (params: PaymentPaginationParams): Promise<PaginatedPaymentDataResult> => {
  try {
    const { page, pageSize, searchQuery, statusFilter } = params;
    const offset = (page - 1) * pageSize;

    let rentalIds: string[] = [];
    let courierIds: string[] = [];

    // Если есть поисковый запрос, найдем ID в связанных таблицах
    if (searchQuery && searchQuery.trim()) {
      logger.debug('Выполняем поиск по связанным таблицам:', searchQuery);
      
      // Поиск rental_id по human_number в таблице rentals
      const { data: rentals } = await supabase
        .from('rentals')
        .select('id')
        .ilike('human_number', `%${searchQuery}%`);
      
      if (rentals) {
        rentalIds = rentals.map(r => r.id);
      }

      // Поиск courier_id по name в таблице couriers
      const { data: couriers } = await supabase
        .from('couriers')
        .select('id')
        .ilike('name', `%${searchQuery}%`);
      
      if (couriers) {
        courierIds = couriers.map(c => c.id);
      }
    }

    // Создаем базовый запрос
    let query = supabase
      .from('payments')
      .select(`
        *,
        rentals (
          human_number
        ),
        couriers (
          name
        )
      `, { count: 'exact' });

    // Добавляем фильтр по статусу
    if (statusFilter && statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    // Добавляем поиск по всем полям
    if (searchQuery && searchQuery.trim()) {
      const searchConditions = [
        `human_number.ilike.%${searchQuery}%`,
        `status.ilike.%${searchQuery}%`,
        `payment_method.ilike.%${searchQuery}%`
      ];

      // Добавляем условия для поиска по связанным таблицам
      if (rentalIds.length > 0) {
        searchConditions.push(`rental_id.in.(${rentalIds.join(',')})`);
      }
      if (courierIds.length > 0) {
        searchConditions.push(`courier_id.in.(${courierIds.join(',')})`);
      }

      query = query.or(searchConditions.join(','));
    }

    // Применяем сортировку и пагинацию
    const { data: paymentsData, error, count } = await query
      .order('payment_date', { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (error) {
      console.error('Ошибка при получении пагинированных платежей:', error);
      throw error;
    }

    // Обработка и форматирование данных
    const payments: Payment[] = (paymentsData || []).map(paymentRecord => ({
      id: paymentRecord.id,
      rentalId: paymentRecord.rental_id || '',
      rentalHumanNumber: paymentRecord.rentals?.human_number || null,
      courierId: paymentRecord.courier_id,
      courierName: paymentRecord.couriers?.name || 'Неизвестный курьер',
      amount: paymentRecord.amount,
      date: paymentRecord.payment_date,
      status: paymentRecord.status as 'paid' | 'pending' | 'error' | 'completed' | 'auth' | 'failed',
      method: paymentRecord.payment_method as 'card' | 'cash' | 'account' | 'sbp',
      human_number: paymentRecord.human_number || null,
      receiptUrl: paymentRecord.receipt_url || null,
      subscription_id: paymentRecord.subscription_id || null
    }));

    const totalCount = count || 0;
    const hasNextPage = offset + pageSize < totalCount;
    const hasPreviousPage = page > 1;

    logger.info(`Получены пагинированные платежи: ${payments.length} из ${totalCount} (страница ${page})`);

    return {
      payments,
      totalCount,
      hasNextPage,
      hasPreviousPage,
      dataSource: 'supabase',
      connectionError: null
    };
  } catch (error: any) {
    console.error('Ошибка при получении пагинированных платежей:', error);
    throw error;
  }
};

/**
 * Получает полную статистику по платежам
 */
export const fetchPaymentStats = async (): Promise<{ 
  payments: Payment[]; 
  connectionError: string | null 
}> => {
  const result = await fetchPaymentData();
  return {
    payments: result.payments,
    connectionError: result.connectionError
  };
};
